import { useEffect, useState } from "react";
import { Navbar, Avatar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet, PieChart, TrendingUp, RefreshCw, Layers, Gift, Copy, Users } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 hover:-translate-y-1 hover:shadow-sm transition-transform">
      <div className="flex items-center gap-2 text-[#5C5C5C]">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className="font-metric text-4xl text-[#0A2E26] mt-3">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [dists, setDists] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [shares, setShares] = useState(1);
  const [buying, setBuying] = useState(false);

  const load = () => {
    api.get("/fund/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/user/distributions").then((r) => setDists(r.data)).catch(() => {});
    api.get("/user/referrals").then((r) => setReferrals(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const referralLink = referrals ? `${window.location.origin}/register?ref=${referrals.referral_code}` : "";
  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  const invest = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        shares: Number(shares),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBuying(false);
    }
  };

  const toggleReinvest = async (checked) => {
    try {
      await api.put("/user/reinvest", { reinvest: checked });
      await refresh();
      toast.success(checked ? "Auto-reinvest enabled" : "Auto-reinvest disabled");
    } catch {
      toast.error("Could not update preference");
    }
  };

  if (!user) return null;
  const ownership = stats && stats.total_shares > 0 ? (user.shares / stats.total_shares) * 100 : 0;
  const invested = user.shares * (stats?.share_price || 100);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4">
          <Avatar user={user} size={56} />
          <div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2E26] tracking-tight">
              Hello, {user.name.split(" ")[0]}
            </h1>
            <p className="text-[#5C5C5C] mt-1 text-lg">Here's how your stake in the fund is doing.</p>
          </div>
        </motion.div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric icon={Layers} label="Shares owned" value={Number(user.shares).toLocaleString(undefined, { maximumFractionDigits: 2 })} accent="#0A2E26" />
          <Metric icon={PieChart} label="Ownership" value={ownership.toFixed(3) + "%"} accent="#C49A45" />
          <Metric icon={TrendingUp} label="Invested" value={fmt(invested)} accent="#2563EB" />
          <Metric icon={Wallet} label="Payout balance" value={fmt(user.balance)} accent="#059669" />
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          {/* Invest card */}
          <div className="lg:col-span-2 bg-white border border-[#E5E5E5] rounded-xl p-7">
            <h2 className="font-display font-bold text-xl text-[#0A2E26]">Buy shares</h2>
            <p className="text-sm text-[#5C5C5C] mt-1">Each share is $100. Buy as many as you like.</p>
            <div className="mt-5 flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs font-semibold text-[#5C5C5C] uppercase">Shares</label>
                <Input
                  data-testid="shares-input"
                  type="number"
                  min={1}
                  max={100}
                  value={shares}
                  onChange={(e) => setShares(e.target.value)}
                  className="mt-1 w-32 bg-white"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-[#5C5C5C] uppercase">Total</p>
                <p className="font-metric text-3xl text-[#0A2E26]">{fmt((Number(shares) || 0) * 100)}</p>
              </div>
              <Button
                data-testid="invest-btn"
                onClick={invest}
                disabled={buying || Number(shares) < 1}
                className="bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md h-11 px-7 ml-auto"
              >
                {buying ? "Redirecting…" : "Invest with Stripe"}
              </Button>
            </div>
          </div>

          {/* Reinvest card */}
          <div className="bg-[#0A2E26] noise-overlay rounded-xl p-7">
            <RefreshCw className="w-7 h-7 text-[#C49A45]" />
            <h2 className="font-display font-bold text-xl text-white mt-3">Auto-reinvest</h2>
            <p className="text-sm text-white/70 mt-1">
              Turn yearly returns into more shares automatically.
            </p>
            <div className="mt-5 flex items-center justify-between bg-white/10 rounded-lg px-4 py-3">
              <span className="text-white text-sm font-medium">
                {user.reinvest ? "Reinvesting profits" : "Paying out to balance"}
              </span>
              <Switch data-testid="reinvest-switch" checked={user.reinvest} onCheckedChange={toggleReinvest} />
            </div>
          </div>
        </div>

        {/* Referral card */}
        <div className="mt-6 bg-white border border-[#E5E5E5] rounded-xl p-7" data-testid="referral-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#C49A45]" />
                <h2 className="font-display font-bold text-xl text-[#0A2E26]">Invite friends</h2>
              </div>
              <p className="text-sm text-[#5C5C5C] mt-1 max-w-md">
                Share your personal link. When friends join the fund through it, they show up here.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#0A2E26]" />
              <div className="text-right">
                <p className="font-metric text-4xl text-[#0A2E26]" data-testid="referral-count">
                  {referrals ? referrals.count : 0}
                </p>
                <p className="text-xs text-[#5C5C5C] uppercase tracking-wide">friends joined</p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[240px] bg-[#F9F8F6] border border-[#E5E5E5] rounded-md px-4 py-3 text-sm text-[#5C5C5C] truncate" data-testid="referral-link">
              {referralLink || "Loading…"}
            </div>
            <Button data-testid="copy-referral-btn" onClick={copyLink} disabled={!referralLink}
              className="bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md h-11 px-6 gap-2">
              <Copy className="w-4 h-4" /> Copy link
            </Button>
          </div>

          {referrals && referrals.friends.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="referral-friends">
              {referrals.friends.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-[#E7F6EF] text-[#059669] text-sm font-medium px-3 py-1.5 rounded-full">
                  <span className="w-6 h-6 rounded-full bg-[#059669] text-white flex items-center justify-center text-xs font-bold">
                    {(f.name || "?")[0].toUpperCase()}
                  </span>
                  {f.name}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6 bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h2 className="font-display font-bold text-xl text-[#0A2E26]">Annual returns history</h2>
          </div>
          {dists.length === 0 ? (
            <p data-testid="no-distributions" className="px-6 py-10 text-center text-[#5C5C5C]">
              No returns distributed yet. Payouts appear here each year.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="distributions-table">
              <thead>
                <tr className="text-left text-[#5C5C5C] border-b border-[#E5E5E5]">
                  <th className="px-6 py-3 font-medium">Period</th>
                  <th className="px-6 py-3 font-medium">Shares held</th>
                  <th className="px-6 py-3 font-medium">Action</th>
                  <th className="px-6 py-3 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {dists.map((d) => (
                  <tr key={d.id} className="border-b border-[#E5E5E5] last:border-0">
                    <td className="px-6 py-3 font-medium text-[#0A2E26]">{d.period || `${d.quarter || ""} ${d.year}`}</td>
                    <td className="px-6 py-3 text-[#5C5C5C]">{Number(d.shares).toFixed(2)}</td>
                    <td className="px-6 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${d.action === "reinvested" ? "bg-[#E7F6EF] text-[#059669]" : "bg-[#E8F0FE] text-[#2563EB]"}`}>
                        {d.action}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-semibold text-[#0A2E26]">{fmt(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
